/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { Typography, FormControlLabel, Checkbox } from "@mui/material";
import { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { usePostRequest } from "../../../../../api/helper";
import ArgosButton from "../../../../components/Buttons/ArgosButton";
import ArgosFlexbox from "../../../../components/Containers/ArgosFlexbox";
import ArgosMenuItem from "../../../../components/Form/Select/ArgosMenuItem";
import ArgosSelect from "../../../../components/Form/Select/ArgosSelect";
import ArgosTextField from "../../../../components/Form/TextField/ArgosTextField";
import {hasThisAccessLevelToRouteKey, getIsUserAdmin, useGetIsUserAdmin, useHasWriteAccess} from "../../../../../redux/ducks/AccessControl/accessControlHelpers";
import {EArgosRouteKeys} from "../../../../../Routes/constants";
import {EUserPermissionAccess} from "../../../../../redux/ducks/AccessControl/types";
import VAR_COMMON_CONSTANTS from "../../../../../constants/globalVariables"
import {stringToDateToLocaleString} from "../../../../../helpers/helpers";
import { useAppSelector } from "../../../../../redux/hooks";
import { selectCurrentUser } from "../../../../../redux/ducks/AccessControl/slice";
import { allowAdminOrOwnerPermissions, determineSaveState, planOfRecordDisablePermissions } from "../../../../../helpers/permissionHelpers";
import { checkPlanOfRecordEndpoint } from "../../constants";
import { handleCheck, renderCheckAdasLinkDialog } from "../../Table/Dialogs/EditStudyDialog";

export function convertUTCDateToLocalDate(date: any) {
  var newDate = date; //We are already in UTC
  var offset = date.getTimezoneOffset() / 60; //offset in hours
  var hours = date.getHours();

  newDate.setHours(hours - offset);

  return newDate;
}

const Description = (props: any) => {
  const fetchLinkedHandle = usePostRequest(checkPlanOfRecordEndpoint)
  const [isProcessing, setIsProcessing] = useState<boolean>(false)
  const [openDialog, setOpenDialog] = useState<boolean>(false)
  const [linkedData, setLinkedData] = useState<Record<string, any>>({})
  const isReadOnlyStudy: boolean | undefined = props?.isReadOnlyStudy
  const studyArchive=props?.studyData?.isStudyArchive
  const [prevStudyName, setPrevStudyName] = useState(props?.studyData?.name);
  const [prevStudyOwner, setPrevStudyOwner] = useState<any>(props?.studyData?.owner); // Owner retrieved from the database
  const [prevStudyDescription, setPrevStudyDescription] = useState(props?.studyData?.description);
  const [studyName, setStudyName] = useState("props?.studyData?.name");
  const [prevLastModified, setPrevLastModified] = useState("");
  const [isPlanOfRecord, setCheckedIsPlanOfRecord] = useState<boolean>(props?.studyData?.isPlanOfRecord)
  const [prevIsPlanOfRecord, setPrevStudyPlanOfRecord] = useState();
  const [isStudyArchive, setCheckedIsStudyArchive] = useState<boolean>(props?.studyData?.isStudyArchive)
  const [prevIsStudyArchive, setPrevStudyArchive] = useState();

  const [description, setStudyDescription] = useState("");
  const [dateCreated, setDateCreated] = useState(props?.studyData?.dateCreated);
  const [lastModified, setLastModified] = useState("");
  const fetchHandle = usePostRequest("/HttpTriggerGetDataCoreSQL?container=Users");
  const [ownerList, setPossibleOwnersList] = useState([]);
  const hasWriteAccess = useHasWriteAccess();
  const [owner, setOwner] = useState<any>(""); // Current selected Owner from dropdown menu
  const [userIsOwner, setUserIsOwner] = useState(false);
  const gmid = useAppSelector(state => selectCurrentUser(state).id); // User's GMID
  const isUserAdmin = useGetIsUserAdmin();

  /**
   * Update study info on tab change (which changes props), in case it was updated in another tab (case: last date modified)
   */
  useEffect(() => {
    setLastModified(props?.studyData?.dateModified);
    setStudyDescription(props?.studyData?.description);
    setStudyName(props?.studyData?.name);
    setCheckedIsPlanOfRecord(props?.studyData?.isPlanOfRecord)
    setCheckedIsStudyArchive(props?.studyData?.isStudyArchive)
  }, [props])

  /**
   * Refreshes UI if the owner of the study changes
   */
  useEffect(() => {
    setUserIsOwner(gmid === prevStudyOwner)
  } , [prevStudyOwner])

  useEffect(() => {
    fetchData()
  }, [])
  
  useEffect(() => {
    const ownerData: any = ownerList?.filter((ownerData: any) => ownerData?.id == props?.studyData?.owner)[0]
    if (ownerData?.id !== undefined) {
      setOwner(`${ownerData?.id}`)
    }
  }, [ownerList, props])

  const fetchData = () => {
    fetchHandle().
      then((data) => {
        createOwnersList(data);
      })
      .catch((e) => {
        toast.error("Error querying the database");
        console.log(e)
      })
  }
  const createOwnersList = (data: any[]) => {
    const filteredUsers: any = data.filter((userInfo: any) => hasThisAccessLevelToRouteKey(EArgosRouteKeys.COVERAGE_REQUIREMENTS, userInfo, EUserPermissionAccess.READ_WRITE))
    const reformatUsers: [] = filteredUsers.map((user: any) =>
    ({
      "id": user["id"],
      "firstName": user["firstName"],
      "lastName": user["lastName"],
      "email": user["email"]
    })
    )
    setPossibleOwnersList(reformatUsers)
  }
  const user = useAppSelector(selectCurrentUser)
  const isAdmin = getIsUserAdmin(user)
  const handleEditStudy = () => {
    // Check if any of the study info has changed and update accordingly
    const dataToEdit: any = {}
    let newOwnerData: any = ownerList.filter((element: any) => element.id == owner)
    if (prevStudyName != studyName) {
      dataToEdit["name"] = studyName
    }
    if (prevStudyDescription != description) {
      dataToEdit["description"] = description
    }
    if (newOwnerData[0].id !== undefined && prevStudyOwner != newOwnerData[0].id) {
      dataToEdit["owner"] = newOwnerData[0].id
      setPrevStudyOwner(newOwnerData[0].id)
    }
    if (prevIsPlanOfRecord != isPlanOfRecord) {
      dataToEdit["isPlanOfRecord"] = isPlanOfRecord
    }
    if (prevIsStudyArchive != isStudyArchive) {
      dataToEdit["isStudyArchive"] = isStudyArchive
    }
    if (prevLastModified != lastModified) {
      dataToEdit["dateModified"] = lastModified
    }

    toast.success("Changes Saved");

    const reqBody = {
      "data": dataToEdit,
      "id": props?.studyData?.id,
      "fromDetails": true
    }
    if (Object.keys(dataToEdit).length != 0) {
      props.editStudy(reqBody)
    }
  }
  return (
    <div>
      {/* Dialog check for plan of record link to Adas */}
      {renderCheckAdasLinkDialog(
        openDialog,
        setOpenDialog,
        isProcessing,
        linkedData,
        setLinkedData,
        setCheckedIsPlanOfRecord
      )}
      <ArgosFlexbox vertical style={{ width: "40%" }}>
        <ArgosTextField 
          disabled={isReadOnlyStudy || !hasWriteAccess}
          value={studyName} 
          type="text"
          onChange={(e) => setStudyName(e.target.value)}
          label={VAR_COMMON_CONSTANTS.VAR_DATA_SET+" Name"}
        />
        <ArgosFlexbox style={{ alignItems: "center" }}>
          <Typography>Owner:</Typography>
          <ArgosSelect
            disabled={isReadOnlyStudy || (!isUserAdmin && !userIsOwner)}
            style={{ flexGrow: 1 }}
            value={owner}
            onChange={(e) => setOwner(e.target.value)}>
              {ownerList.map((element: any, index: number) => 
                <ArgosMenuItem key={index} value={`${element?.id}`}> 
                  {`${element?.firstName} ${element?.lastName} (${element?.email})`}
                </ArgosMenuItem>)}
          </ArgosSelect>
        </ArgosFlexbox>

        <ArgosTextField 
          disabled={isReadOnlyStudy || !hasWriteAccess}
          multiline={true}
          rows={4}
          value={description} 
          type="text"
          onChange={(e) => setStudyDescription(e.target.value)}
          label="Description"
        />
        <ArgosTextField 
          disabled={isReadOnlyStudy || !hasWriteAccess}
          value={stringToDateToLocaleString(dateCreated)} 
          type="text"
          label="Date Created"
        />
        <ArgosTextField 
          disabled={isReadOnlyStudy || !hasWriteAccess}
          value={stringToDateToLocaleString(lastModified)} 
          type="text"
          label="Date Last Modified"
        />
        <FormControlLabel
          control={
            <Checkbox
              onChange={(event, checked) => handleCheck(
                { target: { checked } },
                isPlanOfRecord,
                setIsProcessing,
                setOpenDialog,
                fetchLinkedHandle,
                props?.studyData?.id,
                setLinkedData,
                setCheckedIsPlanOfRecord
              )}
              checked={isPlanOfRecord}
              id="outlined-basic"
              disabled={isReadOnlyStudy || planOfRecordDisablePermissions(hasWriteAccess, owner, gmid)}
            />
          }
          label={'Plan of Record'}
          align-items="center"
          style={{paddingBottom: "20px"}}
        />
        <FormControlLabel
          control={
            <Checkbox
            onChange={(e) => {
              setCheckedIsStudyArchive(e.target.checked);
            }}
              checked={isStudyArchive}
              id="outlined-basic"
              disabled={allowAdminOrOwnerPermissions(isAdmin, owner, gmid)}
            />
          }
          label={'Archive'}
          align-items="center"
          style={{paddingBottom: "20px"}}
        />
      </ArgosFlexbox>
      {hasWriteAccess && <ArgosButton  disabled={determineSaveState(isStudyArchive, studyArchive)}  onClick={handleEditStudy}> Save Changes</ArgosButton>}
    </div>

  )
}
export default Description;